import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  emptyLabel?: string;
}

/**
 * Dark-themed multi-select with a checkbox dropdown and optional search.
 * An empty selection conventionally means "applies to all".
 */
const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'All (any)',
  searchable = true,
  emptyLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, query]);

  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;

  const toggle = (v: string) => {
    if (selectedSet.has(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input flex items-center justify-between gap-2 text-left w-full"
      >
        <span className="flex-1 truncate">
          {value.length === 0 ? (
            <span className="text-slate-500">{emptyLabel ?? placeholder}</span>
          ) : (
            <span className="text-white">
              {value.length <= 2
                ? value.map(labelFor).join(', ')
                : `${value.length} selected`}
            </span>
          )}
        </span>
        {value.length > 0 && (
          <span
            role="button"
            tabIndex={-1}
            onClick={clearAll}
            className="text-slate-400 hover:text-white text-xs px-1"
            aria-label="Clear selection"
          >
            ✕
          </span>
        )}
        <span className="text-slate-500 text-xs">▼</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg shadow-2xl max-h-72 overflow-hidden flex flex-col">
          {searchable && (
            <div className="p-2 border-b border-slate-800">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="input py-1.5 text-sm w-full"
                autoFocus
              />
            </div>
          )}
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">No matches</div>
            ) : (
              filtered.map((o) => {
                const checked = selectedSet.has(o.value);
                return (
                  <label
                    key={o.value}
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-800/60"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.value)}
                      className="accent-indigo-500"
                    />
                    <span className={checked ? 'text-white' : 'text-slate-300'}>{o.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
